import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  filterHermesOptions,
  filterN8nOptions,
  hermesFleets,
  type HermesSkillOption,
  type N8nWorkflowOption,
} from '../domain/addExisting';
import { LOOP_CADENCES, LOOP_CADENCE_LABELS, loopDraftToInput, type LoopDraft } from '../domain/loopBoard';
import type { CreateLoopInput } from '../../../apps/apis/commandCenter/types';

// 'plan' leads: pressing "+" should capture what we intend to build before offering to register
// something that already exists.
type Source = 'plan' | 'hermes' | 'n8n';

interface AddExistingDialogProps {
  open: boolean;
  /** Section the managed skill card lands in. */
  sectionTitle: string;
  /** Section key stored on a filed loop; the title is for display only. */
  sectionSlug: string;
  hermesOptions: HermesSkillOption[];
  n8nOptions: N8nWorkflowOption[];
  /** Live workflow source state, kept separate from a valid empty result. */
  n8nState: 'loading' | 'error' | 'ready';
  isPending: boolean;
  onCancel: () => void;
  /** Adding a Hermes skill creates only its monitoring card. */
  onPickHermes: (option: HermesSkillOption) => void;
  /** Adding an n8n workflow creates only its monitoring card. */
  onPickN8n: (option: N8nWorkflowOption) => void;
  /** Files a planned loop. It lands at `idea`; nothing is registered or scheduled by this. */
  onPlanLoop: (input: CreateLoopInput) => void;
  /**
   * 'add' registers a monitoring card. 'connect' says which skill a loop became: only Hermes skills
   * are on offer, because a loop is built as a skill; planning is not on offer; and a skill already
   * carrying a card stays pickable — being on the board does not stop it from being what this became.
   */
  mode?: 'add' | 'connect' | undefined;
  /** The loop being connected, named so nobody wonders what they are picking for. */
  connectTargetName?: string | undefined;
}

/** Registers a managed Hermes skill or live n8n workflow as a monitoring card. */
const AddExistingDialog = ({
  open,
  sectionTitle,
  sectionSlug,
  hermesOptions,
  n8nOptions,
  n8nState,
  isPending,
  onCancel,
  onPickHermes,
  onPickN8n,
  onPlanLoop,
  mode = 'add',
  connectTargetName,
}: AddExistingDialogProps) => {
  const connecting = mode === 'connect';
  const [source, setSource] = useState<Source>(connecting ? 'hermes' : 'plan');
  const [draft, setDraft] = useState<LoopDraft>({
    name: '',
    objective: '',
    acceptance: '',
    owner: '',
    cadence: '',
    targetDate: '',
    section: sectionSlug,
  });
  const [hermesFleet, setHermesFleet] = useState('');
  const [hermesQuery, setHermesQuery] = useState('');
  const [n8nQuery, setN8nQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    dialogRef.current?.querySelector<HTMLElement>('button, select, input')?.focus();
  }, [open]);

  if (!open) {
    return null;
  }

  const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }
    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), select:not(:disabled), input:not(:disabled)',
      ),
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const setDraftField = (patch: Partial<LoopDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const fileLoop = () => {
    if (!draft.name.trim()) {
      return;
    }
    onPlanLoop(loopDraftToInput({ ...draft, section: sectionSlug }));
    setDraft({ name: '', objective: '', acceptance: '', owner: '', cadence: '', targetDate: '', section: sectionSlug });
  };

  const fleets = hermesFleets(hermesOptions);
  // Planning shows the form and nothing else; the picker belongs to the two registration tabs.
  const rows =
    source === 'plan'
      ? []
      : source === 'hermes'
        ? filterHermesOptions(hermesOptions, hermesFleet, hermesQuery).map((o) => ({
            key: o.cardId,
            title: o.name,
            subtitle: o.description ?? o.origin,
            add: () => onPickHermes(o),
            addDisabled: connecting ? false : o.onBoard,
          }))
        : filterN8nOptions(n8nOptions, n8nQuery).map((o) => ({
            key: o.cardId,
            title: o.name,
            subtitle: o.workflowId,
            add: () => onPickN8n(o),
            addDisabled: connecting ? false : o.onBoard,
          }));

  return (
    <div role="presentation" className="overlay open" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div
        ref={dialogRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Add managed skill"
        onKeyDown={trapFocus}
      >
        <div className="dlg-h">
          <div className="dlg-t">{connecting ? `Connect ${connectTargetName ?? 'loop'}` : 'Add managed skill'}</div>
          <div className="dlg-d">
            {connecting
              ? `Pick the skill or workflow “${connectTargetName ?? 'this loop'}” became. One with no card yet gets one in “${sectionTitle}”.`
              : `Pick a live n8n workflow or parent Hermes skill to monitor in “${sectionTitle}”.`}
          </div>
        </div>

        <div className="dlg-b">
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }} hidden={connecting}>
            {connecting ? null : (
              <button
                type="button"
                className={`btn ${source === 'plan' ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={source === 'plan'}
                onClick={() => setSource('plan')}
              >
                Plan a loop
              </button>
            )}
            <button
              type="button"
              className={`btn ${source === 'hermes' ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={source === 'hermes'}
              onClick={() => setSource('hermes')}
            >
              Hermes skill
            </button>
            {connecting ? null : (
              <button
                type="button"
                className={`btn ${source === 'n8n' ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={source === 'n8n'}
                onClick={() => setSource('n8n')}
              >
                n8n workflow
              </button>
            )}
          </div>

          {source === 'plan' ? (
            <div className="plan-form">
              <label className="fld">
                <span>Loop name</span>
                <input
                  className="inp"
                  placeholder="e.g. Churn Save Loop"
                  value={draft.name}
                  onChange={(e) => setDraftField({ name: e.target.value })}
                />
              </label>
              <label className="fld">
                <span>What should it accomplish?</span>
                <textarea
                  className="inp"
                  rows={3}
                  placeholder="What should it accomplish?"
                  value={draft.objective}
                  onChange={(e) => setDraftField({ objective: e.target.value })}
                />
              </label>
              <label className="fld">
                <span>How will we know it is working?</span>
                <textarea
                  className="inp"
                  rows={2}
                  placeholder="the number that should move, e.g. disputes answered within a day"
                  value={draft.acceptance}
                  onChange={(e) => setDraftField({ acceptance: e.target.value })}
                />
              </label>
              <label className="fld">
                <span>Who owns it?</span>
                <input
                  className="inp"
                  placeholder="email name, e.g. devina"
                  value={draft.owner}
                  onChange={(e) => setDraftField({ owner: e.target.value })}
                />
              </label>
              <label className="fld">
                <span>How often should it run?</span>
                <select
                  className="inp"
                  value={draft.cadence}
                  onChange={(e) => setDraftField({ cadence: e.target.value })}
                >
                  <option value="">Not decided yet</option>
                  {LOOP_CADENCES.map((cadence) => (
                    <option key={cadence} value={cadence}>
                      {LOOP_CADENCE_LABELS[cadence]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fld">
                <span>Live by (optional)</span>
                <input
                  className="inp"
                  type="date"
                  value={draft.targetDate}
                  onChange={(e) => setDraftField({ targetDate: e.target.value })}
                />
              </label>
              {/* Blanks are allowed on purpose: the board marks the loop "needs detail" rather than
                  blocking someone from writing the idea down while they have it. A missing date is
                  not even that — not having picked a day is normal for an idea. */}
              <button type="button" className="btn btn-primary" disabled={isPending} onClick={fileLoop}>
                File this loop
              </button>
            </div>
          ) : null}

          {source === 'hermes' ? (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <select
                className="inp"
                aria-label="Fleet"
                value={hermesFleet}
                onChange={(e) => setHermesFleet(e.target.value)}
                style={{ maxWidth: 220 }}
              >
                <option value="">All sources</option>
                {fleets.map((fleet) => (
                  <option key={fleet} value={fleet}>
                    {fleet}
                  </option>
                ))}
              </select>
              <input
                className="inp"
                placeholder="Search skills…"
                aria-label="Search skills"
                value={hermesQuery}
                onChange={(e) => setHermesQuery(e.target.value)}
              />
            </div>
          ) : null}

          {source === 'n8n' ? (
            <div style={{ marginBottom: 10 }}>
              <input
                className="inp"
                placeholder="Search workflows…"
                aria-label="Search workflows"
                value={n8nQuery}
                onChange={(e) => setN8nQuery(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          ) : null}

          {source === 'plan' ? null : (
            <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rows.length === 0 ? (
                <div className="dlg-d" style={{ padding: '12px 0' }}>
                  {source === 'hermes'
                    ? 'No managed Hermes skills available to add.'
                    : n8nState === 'loading'
                      ? 'Loading live n8n workflows…'
                      : n8nState === 'error'
                        ? 'Could not load live n8n workflows.'
                        : 'No live n8n workflows available to add.'}
                </div>
              ) : (
                rows.map((row) => (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '8px 10px',
                      border: '1px solid var(--line, #2a2a2a)',
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {row.title}
                      </div>
                      <div className="dlg-d" style={{ fontSize: 12 }}>
                        {row.subtitle}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        data-testid={`add-${row.key}`}
                        aria-label={`${connecting ? 'Connect' : 'Add'} ${row.title}`}
                        disabled={row.addDisabled || isPending}
                        onClick={row.add}
                      >
                        {connecting ? 'Connect' : 'Add'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="dlg-f">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddExistingDialog;
